import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {Tabs, Tab, createTheme, ThemeProvider} from '@mui/material';
import styled from '@emotion/styled';
import {theme as defaultTheme} from '../../../themes/default-theme';

const customTheme = createTheme({
  ...defaultTheme,
  components: {
    MuiTabs: {
      styleOverrides: {
        list: {
          justifyContent: 'center',
          '&:hover': {
            outline: 'none',
          },
          '& .MuiTabs-scroller': {
            flex: 'initial',
            margin: '0 auto',
          }
        },
        indicator: {
          height: '4px',
          backgroundColor: defaultTheme.palette.vividBlue,
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: '15px',
          fontWeight: 800,
          lineHeight: 1.2,
          color: defaultTheme.palette.gunmentalGrey,
          textTransform: 'capitalize',
          opacity: 1,
          borderBottom: '4px solid #D9D9D9',
          minWidth: 160,
          
          [defaultTheme.breakpoints.down('sm')]: {
            fontSize: '12px',
            minWidth: 'auto'
          },

          '&:hover': {
            color: defaultTheme.palette.charcoal,
          },

          '&.Mui-selected': {
            color: defaultTheme.palette.vividBlue,
          }
        }
      }
    }
  }
});

const TabsWrapper = styled.div`
  ${defaultTheme.breakpoints.down('md')} {
    position: relative;
    &:after {
      content: "";
      background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%);
      position: absolute;
      right: 0;
      top: 0;
      width: 30px;
      height: 100%;
      pointer-events: none;
    }
    .MuiTabs-scroller button:last-child {
      padding-right: 30px;
    }
  }
`;

const TabsContainer = styled(Tabs)`

  .MuiTabs-scroller {

    flex: initial;
    margin: 0 auto;
  }
    
  .MuiTabs-list  {
    justify-content: start;

  }
`;

const TabSelect = styled(Tab)``;

export const TabSelection = props => {
  const {data, variant = 'scrollable', activeIndex = 0} = props;

  const [value, setValue] = React.useState(activeIndex);
  const [windowWidth, setWindowWidth] = React.useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  // Sync internal state with prop changes
  useEffect(() => {
    setValue(activeIndex);
  }, [activeIndex]);

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    props.onChange(newValue);
  };

  // Use scrollable on mobile, centered on desktop
  const isMobile = windowWidth <= 840;
  const tabVariant = isMobile ? 'scrollable' : variant;
  // const shouldBeCentered = !isMobile && (data && data.length <= 4); // Center only if desktop and few tabs

  return (
    <ThemeProvider theme={customTheme}>
      <TabsWrapper>
        <TabsContainer
          value={value}
          onChange={handleChange}
          variant={tabVariant}
          centered={false}
          scrollButtons="auto"
          allowScrollButtonsMobile={false}
        >
          {data && data.map(function(tab, index) {
            return (
              <TabSelect
                key={tab.id || index}
                label={tab.title} 
              />
            )
          })}
        </TabsContainer>
      </TabsWrapper>
    </ThemeProvider>
  );
}

TabSelection.propTypes = {
  data: PropTypes.array,
  variant: PropTypes.string,
  onChange: PropTypes.func,
  activeIndex: PropTypes.number
};

TabSelection.defaultProps = {
  variant: 'scrollable',
  onChange: () => {},
  activeIndex: 0
};

export default TabSelection;
